// The dashboard "getting started" checklist (docs/10 #2.5). Renders three
// states depending on real progress: nothing (org isn't new, or dismissed),
// the full 4-step card, or — once every step is done — a slim dismissable
// pill so it doesn't linger and take up space once it's served its purpose.

import { useNavigate } from '@tanstack/react-router';
import { Button } from '../components/ui.tsx';
import { CheckMark } from '../components/StatusMarks.tsx';
import { useOnboarding, type OnboardingStep, type OnboardingStepId } from './useOnboarding.ts';

export function GettingStartedCard() {
  const state = useOnboarding();

  if (state.isLoading || !state.isYoungOrg || state.checklistDismissed) return null;

  if (state.allDone) {
    return (
      <div className="gs-pill" data-testid="gs-pill">
        <span>
          All set — {state.total}/{state.total} getting-started steps done.
        </span>
        <button
          type="button"
          className="gs-dismiss"
          aria-label="Dismiss getting-started progress"
          onClick={state.dismissChecklist}
        >
          ×
        </button>
      </div>
    );
  }

  return (
    <div className="card card-pad gs-card" data-testid="gs-card">
      <div className="gs-head">
        <div>
          <h2>Getting started</h2>
          <div className="subtitle">Four steps to your first screen showing content.</div>
        </div>
        <div className="row" style={{ alignItems: 'flex-start' }}>
          <span className="gs-count">
            {state.completedCount}/{state.total}
          </span>
          <button
            type="button"
            className="gs-dismiss"
            aria-label="Dismiss getting-started checklist"
            onClick={state.dismissChecklist}
          >
            ×
          </button>
        </div>
      </div>
      <div className="gs-steps">
        {state.steps.map((step) => (
          <StepRow key={step.id} step={step} />
        ))}
      </div>
    </div>
  );
}

/** Each step deep-links to a real route + search params the target page already reads. */
function useStepNavigate() {
  const navigate = useNavigate();
  return (id: OnboardingStepId) => {
    switch (id) {
      case 'pair':
        return navigate({ to: '/screens', search: { pair: true } });
      case 'media':
        return navigate({ to: '/media' });
      case 'playlist':
        return navigate({ to: '/playlists' });
      case 'schedule':
        return navigate({ to: '/schedules', search: { create: true } });
    }
  };
}

function StepRow({ step }: { step: OnboardingStep }) {
  const goTo = useStepNavigate();
  return (
    <div className={`gs-step${step.done ? ' done' : ''}`}>
      <span className="gs-step-mark" aria-hidden="true">
        {step.done ? <CheckMark /> : ''}
      </span>
      <div className="gs-step-body">
        <div className="gs-step-title">{step.title}</div>
        <div className="gs-step-desc">{step.description}</div>
      </div>
      {!step.done && (
        <div className="gs-step-cta">
          <Button size="sm" onClick={() => goTo(step.id)}>
            {step.ctaLabel}
          </Button>
        </div>
      )}
    </div>
  );
}
